import { useState, useCallback } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { showErrorToast, getErrorMessage } from '@/lib/toast-utils';
import { ContentArea } from './useContentAreas';

export interface Product {
  id: string;
  variantId: string;
  name: string;
  shortDescription: string;
  number: string;
  stockLevel: number;
  neverOutOfStock: boolean;
  discontinued: boolean;
  replacementProductId?: string;
  price: number;
  priceFormatted: string;
  imageUrl?: string;
  productType: string;
}

// Module-level cache keyed by query string
const searchCache = new Map<string, { products: Product[]; totalCount: number }>();

export function clearProductSearchCache() {
  searchCache.clear();
}

export const useProductSearch = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { makeAuthenticatedCall } = useAuthenticatedApi();

  const searchProducts = useCallback(async (groupId?: string, query?: string) => {
    const cacheKey = `${groupId || ''}::${query || ''}`;
    const cached = searchCache.get(cacheKey);
    if (cached) {
      setProducts(cached.products);
      setTotalCount(cached.totalCount);
      return cached.products;
    }

    try {
      setIsLoading(true);
      setError(null);

      console.log('Searching products with query:', query, 'groupId:', groupId);
      
      const { data, error: apiError } = await makeAuthenticatedCall<any>('search-products-by-query', {
        query: query || '',
        groupId: groupId || '',
        lightweight: true,
        pageSize: 5,
      });
      
      if (apiError) {
        console.error('Error from search-products function:', apiError);
        setError(apiError);
        showErrorToast('Failed to search products');
        setProducts([]);
        return [];
      }

      if (!data) {
        console.error('No data returned from search-products');
        setError('No data returned');
        setProducts([]);
        return [];
      }

      console.log('Received product data:', data);
      
      if (data.products && Array.isArray(data.products)) {
        console.log('Found products array:', data.products.length);
        const mappedProducts: Product[] = data.products.map((p: any) => ({
          id: p.id || p.productId,
          variantId: p.variantId || p.productVariantId || '',
          name: p.name || p.productName,
          shortDescription: p.shortDescription || '',
          number: p.number || p.productNumber,
          stockLevel: p.stock || p.stockLevel || 0,
          neverOutOfStock: p.neverOutOfStock ?? false,
          discontinued: p.discontinued || false,
          replacementProductId: p.replacementProductId,
          price: p.price || 0,
          priceFormatted: p.priceFormatted || '',
          imageUrl: p.primaryImage,
          productType: p.productType || '',
        }));
        const total = data.pagination?.totalCount ?? mappedProducts.length;
        searchCache.set(cacheKey, { products: mappedProducts, totalCount: total });
        setProducts(mappedProducts);
        setTotalCount(total);
        return mappedProducts;
      } else {
        console.error('Unexpected data format:', typeof data);
        setError('Invalid data format received');
        showErrorToast('Invalid response format');
        setProducts([]);
        return [];
      }
    } catch (err) {
      console.error('Error searching products:', err);
      setError(getErrorMessage(err, 'Unknown error'));
      showErrorToast('Failed to search products');
      setProducts([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [makeAuthenticatedCall]);

  return {
    products,
    totalCount,
    isLoading,
    error,
    searchProducts,
  };
};
